import React, { useEffect, useRef } from 'react';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import TileWMS from 'ol/source/TileWMS';
import GeoJSON from 'ol/format/GeoJSON';
import { Style, Stroke, Fill } from 'ol/style';
import { FullScreen, defaults as defaultControls } from 'ol/control';
import { fromLonLat, transform } from 'ol/proj';
import { getCenter } from 'ol/extent';
import { Point } from 'ol/geom';

type Props = {
  baseMap: 'openstreet' | 'voyager' | 'google' | 'topo';
  customCenter?: {
    cusCenter: [number, number];
  };
  zoomChange?: number;
  type?: 'point' | 'polygon';
  initialLongitude?: number;
  initialLatitude?: number;
  onRiverSelected?: (any) => void;
};

const IMAGE_EXTENT = [
  9018917.1074,
  4799022.3839,
  13473626.3819,
  7178965.6965,
];

const GEOSERVER_URL = 'https://geo.infowater.mn/geoserver/satellite';

const RiverSelectorMap: React.FC<Props> = ({
  baseMap,
  customCenter,
  zoomChange = 6,
  type = 'point',
  initialLongitude,
  initialLatitude,
  onRiverSelected,
}) => {
  const mapRef = useRef<Map | null>(null);
  const baseLayersRef = useRef<Record<string, TileLayer> | null>(null);
  const selectedRiverLayerRef = useRef<VectorLayer<VectorSource> | null>(null);

  const createBaseLayers = () => ({
    openstreet: new TileLayer({ source: new OSM() }),
    voyager: new TileLayer({
      source: new OSM(),
    }),
    google: new TileLayer({
      source: new XYZ({
        url: 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
      }),
    }),
    topo: new TileLayer({
      source: new XYZ({
        url: 'https://{a-c}.tile.opentopomap.org/{z}/{x}/{y}.png',
      }),
    }),
  });

  const createRiverLayer = () =>
    new TileLayer({
      source: new TileWMS({
        url: `${GEOSERVER_URL}/wms`,
        params: {
          SERVICE: 'WMS',
          VERSION: '1.1.0',
          REQUEST: 'GetMap',
          LAYERS: 'satellite:River',
          FORMAT: 'image/png',
          SRS: 'EPSG:4326',
          TILED: true,
        },
        serverType: 'geoserver',
        crossOrigin: 'anonymous',
      }),
      zIndex: 30,
    });

  const createSelectedRiverLayer = () =>
    new VectorLayer({
      source: new VectorSource(),
      style: new Style({
        stroke: new Stroke({
          color: '#ff0000',
          width: 3,
        }),
        fill: new Fill({
          color: 'rgba(0, 255, 255, 0.15)',
        }),
      }),
      zIndex: 100,
    });

  const selectRiverByLonLat = async (
    lon: number,
    lat: number,
    map: Map,
    selectedRiverLayer: VectorLayer<VectorSource>
  ): Promise<any | null> => {
    const buffer = 0.01;

    const bbox = [
      lon - buffer,
      lat - buffer,
      lon + buffer,
      lat + buffer,
    ].join(',');

    const url =
      `${GEOSERVER_URL}/ows?` +
      `service=WFS&version=1.0.0&request=GetFeature&` +
      `typeName=satellite:River&outputFormat=application/json&` +
      `srsname=EPSG:4326&bbox=${bbox}`;

    const res = await fetch(url);
    const text = await res.text();

    if (!text.startsWith('{')) {
      throw new Error('GeoServer response is not JSON');
    }

    const data = JSON.parse(text);

    if (!data.features || data.features.length === 0) {
      return null;
    }

    const features = new GeoJSON().readFeatures(data, {
      featureProjection: 'EPSG:3857',
    });

    const clickPoint = new Point(fromLonLat([lon, lat]));

    let selectedFeature =
      features.find(feature =>
        feature.getGeometry()?.intersectsCoordinate(
          clickPoint.getCoordinates()
        )
      ) ?? features[0];

    const source = selectedRiverLayer.getSource();
    source?.clear();
    source?.addFeature(selectedFeature);

    const geometry = selectedFeature.getGeometry();
    if (geometry) {
      map.getView().fit(geometry.getExtent(), {
        padding: [60, 60, 60, 60],
        duration: 800,
        maxZoom: 14,
      });
    }

    const selectedIndex = features.indexOf(selectedFeature);
    return data.features[selectedIndex];
  };

  const handleMapClick = (map: Map, selectedRiverLayer: VectorLayer<VectorSource>) =>
    async (evt: any) => {
      const [lon, lat] = transform(
        evt.coordinate,
        'EPSG:3857',
        'EPSG:4326'
      );

      try {
        const featureJson = await selectRiverByLonLat(lon, lat, map, selectedRiverLayer);
        if (featureJson) {
          onRiverSelected?.({ river: featureJson, longitude: lon, latitude: lat });
        }
      } catch (err) {
        console.error('River select failed', err);
      }

    };

  useEffect(() => {
    const baseLayers = createBaseLayers();
    baseLayersRef.current = baseLayers;

    const center = customCenter
      ? fromLonLat([
          customCenter.cusCenter[0],
          customCenter.cusCenter[1] + (type === 'point' ? 0 : 0.1),
        ])
      : getCenter(IMAGE_EXTENT);

    const map = new Map({
      target: 'selector-map',
      controls: defaultControls().extend([new FullScreen()]),
      layers: [
        ...Object.values(baseLayers),
        createRiverLayer(),
      ],
      view: new View({
        center,
        zoom: zoomChange,
        minZoom: 3,
        maxZoom: 20,
      }),
    });

    const selectedRiverLayer = createSelectedRiverLayer();
    map.addLayer(selectedRiverLayer);

    selectedRiverLayerRef.current = selectedRiverLayer;
    mapRef.current = map;

    map.on('singleclick', handleMapClick(map, selectedRiverLayer));

    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (
      !mapRef.current ||
      !selectedRiverLayerRef.current ||
      initialLongitude == null ||
      initialLatitude == null
    ) {
      return;
    }

    (async () => {
      try {
        const featureJson = await selectRiverByLonLat(
          initialLongitude,
          initialLatitude,
          mapRef.current!,
          selectedRiverLayerRef.current!
        );

        // if (featureJson) {
        //   onRiverSelected?.({ river: featureJson, longitude: initialLongitude, latitude: initialLatitude });
        // }
      } catch (err) {
        console.error('Failed to auto-select river', err);
      }
    })();
  }, [initialLongitude, initialLatitude]);

  useEffect(() => {
    if (!baseLayersRef.current) return;

    Object.entries(baseLayersRef.current).forEach(([key, layer]) => {
      layer.setVisible(key === baseMap);
    });
  }, [baseMap]);

  return (
    <div
      id="selector-map"
      style={{ width: '100%', height: '400px' }}
    />
  );
};

export default RiverSelectorMap;
